import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import styles from './styles.module.scss';
import PlaceholderItem from './components/PlaceholderItem';
import {
  GET_DOCUMENT_PLACEHOLDER_LINKS,
  GET_DEAL_FIELD_GROUPS,
  GET_PLACEHOLDER_VALUE_HISTORY,
} from '../../../api/placeholders/queries';
import { mutations } from '../../../api';

// Window-level store for linked placeholder UUIDs
// CKEditor balloon will check this to prevent editing linked placeholders
declare global {
  interface Window {
    __linkedPlaceholderUUIDs?: Set<string>;
  }
}

interface DeletedLinkSnapshot {
  title: string;
  currentValue: string | null;
  dealFieldGroupId: string | null;
  status: string | null;
  /** The link's OWN history. Deal-field entries are left out: they live on the
   *  group, survive the deletion there, and are merged back in on read. */
  valueHistory: unknown[];
}

/**
 * What each link held when the document deleted it, keyed by placeholder uuid.
 *
 * Deleting the row throws away the value, the deal-field link and the status —
 * the editor's undo can only bring the CONTROL back, so without this the field
 * returns stripped. Module-level because this panel unmounts whenever the user
 * switches to "This Deal" or closes the sidebar, and the undo can come after
 * that; the editor re-announces pending restores when the sidebar remounts.
 * Nothing needs to outlive the page — undo history doesn't either.
 */
const deletedPlaceholderLinks = new Map<string, DeletedLinkSnapshot>();

interface DocumentFieldsListProps {
  documentId: string;
  dealId: string;
  teamId: string;
  isReadOnly?: boolean;
  isCounterparty?: boolean; // Simplified view for counterparty team
  canLinkContacts?: boolean;
  onShowUpgradeModal?: () => void;
}

const DocumentFieldsList: React.FC<DocumentFieldsListProps> = ({
  documentId,
  dealId,
  teamId,
  isReadOnly = false,
  isCounterparty = false,
  canLinkContacts = true,
  onShowUpgradeModal,
}) => {
  // Single accordion - only one item can be expanded at a time
  const [expandedPlaceholderId, setExpandedPlaceholderId] = useState<string | null>(null);
  const apolloClient = useApolloClient();

  const {
    data: documentData,
    loading: documentLoading,
    refetch: refetchDocument,
  } = useQuery(GET_DOCUMENT_PLACEHOLDER_LINKS, {
    variables: { documentId },
    skip: !documentId,
  });

  const { data: dealFieldsData, refetch: refetchDealFields } = useQuery(GET_DEAL_FIELD_GROUPS, {
    variables: { dealId },
    skip: !dealId,
  });

  // Mutation to delete placeholder link from database (used when a placeholder
  // is deleted directly in the document)
  const [removeDocumentPlaceholder] = useMutation(mutations.removeDocumentPlaceholder);
  // `updateDocumentPlaceholder` recreates a link that no longer exists, which is
  // how a row deleted from the document is rebuilt when the user undoes.
  const [updateDocumentPlaceholder] = useMutation(mutations.updateDocumentPlaceholder);


  const rawPlaceholderLinks = documentData?.getDocumentById?.placeholderLinks || [];
  const dealFieldGroups = dealFieldsData?.dealFieldGroups || [];

  // Sort placeholder links alphabetically by title (A-Z) for consistent, intuitive ordering
  const placeholderLinks = useMemo(() => {
    return [...rawPlaceholderLinks].sort((a: any, b: any) => {
      const titleA = (a.placeholder?.title || '').toLowerCase();
      const titleB = (b.placeholder?.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [rawPlaceholderLinks]);

  // Handle accordion toggle - only one open at a time
  const handleToggle = useCallback((placeholderUuid: string) => {
    setExpandedPlaceholderId((current) => (current === placeholderUuid ? null : placeholderUuid));
  }, []);

  // Listen for placeholder clicks (CKEditor OR docx-native) to expand that item.
  // Prefer the placeholder UUID; fall back to matching by NAME — migrated
  // docx-native placeholders carry no uuid in the body SDT, only the alias/name,
  // so a name match is the only link back to the relational field there.
  useEffect(() => {
    const handlePlaceholderClick = (event: CustomEvent) => {
      const { placeholderId, placeholderName } = event.detail || {};
      if (placeholderId && placeholderLinks.some((l: any) => l.placeholder?.uuid === placeholderId)) {
        setExpandedPlaceholderId(placeholderId);
        return;
      }
      if (placeholderName) {
        const byName = placeholderLinks.find(
          (l: any) => (l.placeholder?.title || '').trim().toLowerCase() === String(placeholderName).trim().toLowerCase()
        );
        if (byName?.placeholder?.uuid) setExpandedPlaceholderId(byName.placeholder.uuid);
      }
    };

    window.addEventListener('focusPlaceholderInSidebar' as any, handlePlaceholderClick);
    return () => {
      window.removeEventListener('focusPlaceholderInSidebar' as any, handlePlaceholderClick);
    };
  }, [placeholderLinks]);

  // Listen for placeholder changes from CKEditor to refresh the list
  useEffect(() => {
    const handlePlaceholderDeleted = async (event: CustomEvent) => {
      const { placeholderId, placeholderName } = event.detail || {};
      // A MIGRATED placeholder carries no uuid in the body — only the alias —
      // so the editor can only report its name. Resolve it against the links we
      // already hold rather than dropping the deletion on the floor.
      const resolvedId =
        placeholderId ||
        (placeholderName
          ? placeholderLinks.find(
              (l: any) =>
                (l.placeholder?.title || '').trim().toLowerCase() === String(placeholderName).trim().toLowerCase()
            )?.placeholder?.uuid
          : undefined);
      if (resolvedId) {
        // Remember what the link held before it goes, so an undo can rebuild it.
        const doomed = placeholderLinks.find((l: any) => l.placeholder?.uuid === resolvedId);
        if (doomed) {
          // Recorded BEFORE any await: an undo that arrives while the history
          // request is still in flight must still find something to restore.
          const snapshot: DeletedLinkSnapshot = {
            title: doomed.placeholder?.title || '',
            currentValue: doomed.currentValue ?? null,
            dealFieldGroupId: doomed.dealFieldGroup?.id ?? null,
            status: doomed.status ?? null,
            valueHistory: [],
          };
          deletedPlaceholderLinks.set(resolvedId, snapshot);

          // The history is fetched here rather than carried by the list query:
          // it is a growing JSONB column, and paying for it on every sidebar
          // load to serve the rare delete would be the wrong trade.
          try {
            const { data } = await apolloClient.query({
              query: GET_PLACEHOLDER_VALUE_HISTORY,
              variables: { placeholderUuid: resolvedId, linkableId: documentId },
              fetchPolicy: 'network-only',
            });
            snapshot.valueHistory = (data?.placeholderValueHistory ?? [])
              .filter((entry: any) => entry.scope === 'document')
              .map((entry: any) => ({
                value: entry.value,
                previous: entry.previous,
                user_id: entry.userId,
                user_name: entry.userName,
                cause: entry.cause,
                at: entry.at,
              }))
              .reverse(); // stored oldest-first; the query returns newest-first
          } catch (error) {
            // History is a nicety — never block the deletion on it.
          }
        }
        // Delete the placeholder link from the database. This handles the case
        // where the user deletes the placeholder directly in the document.
        try {
          await removeDocumentPlaceholder({
            variables: {
              placeholderUuid: resolvedId,
              linkableId: documentId,
              linkableType: 'Document',
              teamId,
            },
          });
        } catch (error) {
          // Ignore errors - the link may have already been deleted (e.g., from sidebar)
        }
      }
      // Refetch to sync with backend
      refetchDocument();
    };

    // An undo put a deleted placeholder back in the document. The
    // `document_placeholders` record survived (only the LINK was destroyed), so
    // the row is rebuilt against the same uuid — value, deal-field link and all.
    const handlePlaceholderRestored = async (event: CustomEvent) => {
      const { placeholderId, placeholderName } = event.detail || {};
      const snapshots = deletedPlaceholderLinks;
      const uuid =
        (placeholderId && snapshots.has(placeholderId) && placeholderId) ||
        (placeholderName
          ? [...snapshots.entries()].find(
              ([, snap]) => snap.title.trim().toLowerCase() === String(placeholderName).trim().toLowerCase()
            )?.[0]
          : undefined);
      if (!uuid) return; // nothing of ours was lost — not a deletion we reported
      const snapshot = snapshots.get(uuid)!;
      snapshots.delete(uuid);

      // `updateDocumentPlaceholder` recreates the link only when it is missing
      // and updates it otherwise, so this is safe to send without first proving
      // the row is gone — which the cached list could not do reliably anyway,
      // the delete's refetch may still be in flight.
      try {
        await updateDocumentPlaceholder({
          variables: {
            placeholderUuid: uuid,
            linkableId: documentId,
            linkableType: 'Document',
            teamId,
            currentValue: snapshot.currentValue,
            dealFieldGroupId: snapshot.dealFieldGroupId,
            status: snapshot.status,
            valueHistory: snapshot.valueHistory,
          },
        });
      } catch (error) {
        // Silent failure - the field is in the document; the sidebar refetch
        // below will show whatever the backend actually has.
      }
      refetchDocument();
    };

    const handlePlaceholderInserted = () => {
      // Refetch to pick up the new placeholder
      refetchDocument();
    };

    window.addEventListener('placeholderDeletedInEditor' as any, handlePlaceholderDeleted);
    window.addEventListener('placeholderRestoredInEditor' as any, handlePlaceholderRestored);
    window.addEventListener('placeholderInsertedInEditor' as any, handlePlaceholderInserted);
    return () => {
      window.removeEventListener('placeholderDeletedInEditor' as any, handlePlaceholderDeleted);
      window.removeEventListener('placeholderRestoredInEditor' as any, handlePlaceholderRestored);
      window.removeEventListener('placeholderInsertedInEditor' as any, handlePlaceholderInserted);
    };
  }, [
    refetchDocument,
    documentId,
    teamId,
    apolloClient,
    removeDocumentPlaceholder,
    updateDocumentPlaceholder,
    placeholderLinks,
  ]);

  // Update window-level store of linked placeholder UUIDs for CKEditor balloon
  useEffect(() => {
    const linkedUUIDs = new Set<string>();
    placeholderLinks.forEach((link: any) => {
      if (link.dealFieldGroup && link.placeholder?.uuid) {
        linkedUUIDs.add(link.placeholder.uuid);
      }
    });
    window.__linkedPlaceholderUUIDs = linkedUUIDs;

    // Dispatch event to notify CKEditor that linked placeholders have changed
    window.dispatchEvent(
      new CustomEvent('linkedPlaceholdersUpdated', {
        detail: { linkedUUIDs: Array.from(linkedUUIDs) },
      })
    );

    return () => {
      // Clean up on unmount - properly delete to prevent memory leak
      delete (window as any).__linkedPlaceholderUUIDs;
    };
  }, [placeholderLinks]);

  const handleRefetch = () => {
    refetchDocument();
    refetchDealFields();
  };

  if (documentLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (placeholderLinks.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No fields in this document</p>
        <p className={styles.fieldsSubtitle}>Fields will appear here when added to the document</p>
      </div>
    );
  }

  return (
    <div className={styles.fieldsList}>
      {placeholderLinks.map((link: any) => (
        <PlaceholderItem
          key={link.id}
          placeholderLink={link}
          dealFieldGroups={dealFieldGroups}
          dealId={dealId}
          documentId={documentId}
          teamId={teamId}
          isReadOnly={isReadOnly}
          isCounterparty={isCounterparty}
          canLinkContacts={canLinkContacts}
          onShowUpgradeModal={onShowUpgradeModal}
          isExpanded={expandedPlaceholderId === link.placeholder?.uuid}
          onToggle={() => handleToggle(link.placeholder?.uuid)}
          onUpdate={handleRefetch}
        />
      ))}
    </div>
  );
};

export default DocumentFieldsList;
